import { supabase } from '../lib/supabase';

export interface BackupData {
  metadata: {
    exportDate: string;
    exportVersion: string;
    totalRecords: number;
    groupId?: string;
    groupName?: string;
  };
  user_profiles: any[];
  groups: any[];
  group_members: any[];
  contributions: any[];
  loans: any[];
  loan_payments: any[];
  loan_requests: any[];
  subscriptions: any[];
  subscription_plans: any[];
  payments: any[];
  membership_fee_payments: any[];
  fraud_alerts: any[];
  bank_accounts: any[];
  bank_transactions: any[];
  notifications: any[];
  audit_logs: any[];
}

/**
 * Backup Service - Exports all platform data for super admin
 * Only accessible to super_admin role
 */
export class BackupService {
  /**
   * Export all platform data as JSON
   */
  static async exportAllData(): Promise<BackupData> {
    const exportDate = new Date().toISOString();
    const backupData: BackupData = {
      metadata: {
        exportDate,
        exportVersion: '1.0.0',
        totalRecords: 0,
      },
      user_profiles: [],
      groups: [],
      group_members: [],
      contributions: [],
      loans: [],
      loan_payments: [],
      loan_requests: [],
      subscriptions: [],
      subscription_plans: [],
      payments: [],
      membership_fee_payments: [],
      fraud_alerts: [],
      bank_accounts: [],
      bank_transactions: [],
      notifications: [],
      audit_logs: [],
    };

    try {
      // Fetch user profiles
      try {
        const { data: profiles, error: profilesError } = await supabase
          .from('user_profiles')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (profilesError) throw profilesError;
        backupData.user_profiles = profiles || [];
      } catch (e) {
        console.warn('user_profiles table error:', e);
        backupData.user_profiles = [];
      }

      // Fetch groups
      try {
        const { data: groups, error: groupsError } = await supabase
          .from('groups')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (groupsError) throw groupsError;
        backupData.groups = groups || [];
      } catch (e) {
        console.warn('groups table error:', e);
        backupData.groups = [];
      }

      // Fetch group members
      try {
        const { data: members, error: membersError } = await supabase
          .from('group_members')
          .select('*')
          .order('joined_at', { ascending: false });
        
        if (membersError) throw membersError;
        backupData.group_members = members || [];
      } catch (e) {
        console.warn('group_members table error:', e);
        backupData.group_members = [];
      }

      // Fetch contributions
      try {
        const { data: contributions, error: contributionsError } = await supabase
          .from('contributions')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (contributionsError) throw contributionsError;
        backupData.contributions = contributions || [];
      } catch (e) {
        console.warn('contributions table error:', e);
        backupData.contributions = [];
      }

      // Fetch loans
      try {
        const { data: loans, error: loansError } = await supabase
          .from('loans')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (loansError) throw loansError;
        backupData.loans = loans || [];
      } catch (e) {
        console.warn('loans table not found or error:', e);
        backupData.loans = [];
      }

      // Fetch loan payments (not loan_repayments)
      try {
        const { data: loanPayments, error: loanPaymentsError } = await supabase
          .from('loan_payments')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (loanPaymentsError) throw loanPaymentsError;
        backupData.loan_payments = loanPayments || [];
      } catch (e) {
        console.warn('loan_payments table not found or error:', e);
        backupData.loan_payments = [];
      }

      // Fetch loan requests
      try {
        const { data: loanRequests, error: loanRequestsError } = await supabase
          .from('loan_requests')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (loanRequestsError) throw loanRequestsError;
        backupData.loan_requests = loanRequests || [];
      } catch (e) {
        console.warn('loan_requests table not found or error:', e);
        backupData.loan_requests = [];
      }

      // Fetch subscriptions
      try {
        const { data: subscriptions, error: subscriptionsError } = await supabase
          .from('user_subscriptions')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (subscriptionsError) throw subscriptionsError;
        backupData.subscriptions = subscriptions || [];
      } catch (e) {
        console.warn('user_subscriptions table error:', e);
        backupData.subscriptions = [];
      }

      // Fetch subscription plans
      try {
        const { data: plans, error: plansError } = await supabase
          .from('subscription_plans')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (plansError) throw plansError;
        backupData.subscription_plans = plans || [];
      } catch (e) {
        console.warn('subscription_plans table error:', e);
        backupData.subscription_plans = [];
      }

      // Fetch payments
      try {
        const { data: payments, error: paymentsError } = await supabase
          .from('payments')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (paymentsError) throw paymentsError;
        backupData.payments = payments || [];
      } catch (e) {
        console.warn('payments table error:', e);
        backupData.payments = [];
      }

      // Fetch membership fee payments
      try {
        const { data: feePayments, error: feePaymentsError } = await supabase
          .from('membership_fee_payments')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (feePaymentsError) throw feePaymentsError;
        backupData.membership_fee_payments = feePayments || [];
      } catch (e) {
        console.warn('membership_fee_payments table error:', e);
        backupData.membership_fee_payments = [];
      }

      // Fetch fraud alerts (if table exists)
      try {
        const { data: fraudAlerts, error: fraudError } = await supabase
          .from('fraud_alerts')
          .select('*')
          .order('detected_at', { ascending: false });
        
        if (!fraudError) {
          backupData.fraud_alerts = fraudAlerts || [];
        }
      } catch (e) {
        // Table might not exist, skip
        console.warn('fraud_alerts table not found, skipping');
      }

      // Fetch bank accounts (if table exists)
      try {
        const { data: bankAccounts, error: bankAccountsError } = await supabase
          .from('bank_accounts')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (!bankAccountsError) {
          backupData.bank_accounts = bankAccounts || [];
        }
      } catch (e) {
        console.warn('bank_accounts table not found, skipping');
      }

      // Fetch bank transactions (if table exists)
      try {
        const { data: bankTransactions, error: bankTransactionsError } = await supabase
          .from('bank_transactions')
          .select('*')
          .order('transaction_date', { ascending: false });
        
        if (!bankTransactionsError) {
          backupData.bank_transactions = bankTransactions || [];
        }
      } catch (e) {
        console.warn('bank_transactions table not found, skipping');
      }

      // Fetch notifications
      try {
        const { data: notifications, error: notificationsError } = await supabase
          .from('notifications')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (!notificationsError) {
          backupData.notifications = notifications || [];
        }
      } catch (e) {
        console.warn('notifications table not found, skipping');
      }

      // Fetch audit logs (if table exists)
      try {
        const { data: auditLogs, error: auditLogsError } = await supabase
          .from('admin_audit_logs')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (!auditLogsError) {
          backupData.audit_logs = auditLogs || [];
        }
      } catch (e) {
        console.warn('admin_audit_logs table not found, skipping');
      }

      // Calculate total records
      backupData.metadata.totalRecords = 
        backupData.user_profiles.length +
        backupData.groups.length +
        backupData.group_members.length +
        backupData.contributions.length +
        backupData.loans.length +
        backupData.loan_payments.length +
        backupData.loan_requests.length +
        backupData.subscriptions.length +
        backupData.subscription_plans.length +
        backupData.payments.length +
        backupData.membership_fee_payments.length +
        backupData.fraud_alerts.length +
        backupData.bank_accounts.length +
        backupData.bank_transactions.length +
        backupData.notifications.length +
        backupData.audit_logs.length;

      return backupData;
    } catch (error: any) {
      console.error('Error exporting data:', error);
      throw new Error(`Failed to export data: ${error.message}`);
    }
  }

  /**
   * Export all data for a specific group
   */
  static async exportGroupData(groupId: string): Promise<BackupData> {
    const exportDate = new Date().toISOString();
    
    // First, get the group details
    const { data: groupData, error: groupError } = await supabase
      .from('groups')
      .select('*')
      .eq('id', groupId)
      .single();

    if (groupError || !groupData) {
      throw new Error(`Group not found: ${groupId}`);
    }

    const backupData: BackupData = {
      metadata: {
        exportDate,
        exportVersion: '1.0.0',
        totalRecords: 0,
        groupId: groupId,
        groupName: groupData.name || 'Unnamed Group',
      },
      user_profiles: [],
      groups: [groupData],
      group_members: [],
      contributions: [],
      loans: [],
      loan_payments: [],
      loan_requests: [],
      subscriptions: [],
      subscription_plans: [],
      payments: [],
      membership_fee_payments: [],
      fraud_alerts: [],
      bank_accounts: [],
      bank_transactions: [],
      notifications: [],
      audit_logs: [],
    };

    try {
      // Fetch group members
      try {
        const { data: members, error: membersError } = await supabase
          .from('group_members')
          .select('*')
          .eq('group_id', groupId)
          .order('joined_at', { ascending: false });
        
        if (!membersError && members) {
          backupData.group_members = members || [];
          
          // Fetch user profiles for all members
          const memberUserIds = members.map(m => m.user_id).filter(Boolean);
          if (memberUserIds.length > 0) {
            const { data: profiles, error: profilesError } = await supabase
              .from('user_profiles')
              .select('*')
              .in('id', memberUserIds);
            
            if (!profilesError && profiles) {
              backupData.user_profiles = profiles || [];
            }
          }
        }
      } catch (e) {
        console.warn('Error fetching group members:', e);
      }

      // Fetch contributions for this group
      try {
        const { data: contributions, error: contributionsError } = await supabase
          .from('contributions')
          .select('*')
          .eq('group_id', groupId)
          .order('created_at', { ascending: false });
        
        if (!contributionsError) {
          backupData.contributions = contributions || [];
        }
      } catch (e) {
        console.warn('Error fetching contributions:', e);
      }

      // Fetch loans for this group
      try {
        const { data: loans, error: loansError } = await supabase
          .from('loans')
          .select('*')
          .eq('group_id', groupId)
          .order('created_at', { ascending: false });
        
        if (!loansError) {
          backupData.loans = loans || [];
          
          // Fetch loan payments for these loans
          if (loans && loans.length > 0) {
            const loanIds = loans.map(l => l.id);
            const { data: loanPayments, error: loanPaymentsError } = await supabase
              .from('loan_payments')
              .select('*')
              .in('loan_id', loanIds)
              .order('created_at', { ascending: false });
            
            if (!loanPaymentsError) {
              backupData.loan_payments = loanPayments || [];
            }
          }
        }
      } catch (e) {
        console.warn('Error fetching loans:', e);
      }

      // Fetch loan requests for this group
      try {
        const { data: loanRequests, error: loanRequestsError } = await supabase
          .from('loan_requests')
          .select('*')
          .eq('group_id', groupId)
          .order('created_at', { ascending: false });
        
        if (!loanRequestsError) {
          backupData.loan_requests = loanRequests || [];
        }
      } catch (e) {
        console.warn('Error fetching loan requests:', e);
      }

      // Fetch membership fee payments for this group
      try {
        const { data: feePayments, error: feePaymentsError } = await supabase
          .from('membership_fee_payments')
          .select('*')
          .eq('group_id', groupId)
          .order('created_at', { ascending: false });
        
        if (!feePaymentsError) {
          backupData.membership_fee_payments = feePayments || [];
        }
      } catch (e) {
        console.warn('Error fetching membership fee payments:', e);
      }

      // Fetch subscriptions for group members
      try {
        const memberUserIds = backupData.group_members.map(m => m.user_id).filter(Boolean);
        if (memberUserIds.length > 0) {
          const { data: subscriptions, error: subscriptionsError } = await supabase
            .from('user_subscriptions')
            .select('*')
            .in('user_id', memberUserIds)
            .order('created_at', { ascending: false });
          
          if (!subscriptionsError) {
            backupData.subscriptions = subscriptions || [];
          }
        }
      } catch (e) {
        console.warn('Error fetching subscriptions:', e);
      }

      // Fetch payments for this group (if payments table has group_id)
      try {
        const { data: payments, error: paymentsError } = await supabase
          .from('payments')
          .select('*')
          .eq('group_id', groupId)
          .order('created_at', { ascending: false });
        
        if (!paymentsError) {
          backupData.payments = payments || [];
        }
      } catch (e) {
        // Payments might not have group_id, try fetching by user_id
        try {
          const memberUserIds = backupData.group_members.map(m => m.user_id).filter(Boolean);
          if (memberUserIds.length > 0) {
            const { data: payments, error: paymentsError } = await supabase
              .from('payments')
              .select('*')
              .in('user_id', memberUserIds)
              .order('created_at', { ascending: false });
            
            if (!paymentsError) {
              backupData.payments = payments || [];
            }
          }
        } catch (e2) {
          console.warn('Error fetching payments:', e2);
        }
      }

      // Calculate total records
      backupData.metadata.totalRecords = 
        backupData.user_profiles.length +
        backupData.groups.length +
        backupData.group_members.length +
        backupData.contributions.length +
        backupData.loans.length +
        backupData.loan_payments.length +
        backupData.loan_requests.length +
        backupData.subscriptions.length +
        backupData.subscription_plans.length +
        backupData.payments.length +
        backupData.membership_fee_payments.length;

      return backupData;
    } catch (error: any) {
      console.error('Error exporting group data:', error);
      throw new Error(`Failed to export group data: ${error.message}`);
    }
  }

  /**
   * Download backup data as JSON file
   */
  static downloadBackup(data: BackupData, filename?: string): void {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || `nasuman-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Export specific table as CSV
   */
  static exportTableAsCSV(data: any[], tableName: string): void {
    if (!data || data.length === 0) {
      alert(`No data available for ${tableName}`);
      return;
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);
    
    // Create CSV content
    const csvRows = [
      headers.join(','), // Header row
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle null, undefined, objects, arrays
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return JSON.stringify(value).replace(/"/g, '""');
          return String(value).replace(/"/g, '""').replace(/,/g, ';');
        }).join(',')
      )
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${tableName}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
